import { processConversation } from './profileImage';
import { formatVectorDBTimestampWithDate, parseVectorDbUtc } from './timeZone';
import { foldReactionMessages } from './messageReactions';

/**
 * Parse a timestamp string as UTC and return a Date object.
 * Agent Service returns timestamps like "2025-09-23T05:38:01.399270" which are UTC but may be missing 'Z'.
 */
function parseUtcTimestamp(timestamp: string | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  
  // Use the parseVectorDbUtc helper which handles missing 'Z' suffix
  const parsed = parseVectorDbUtc(timestamp);
  if (parsed) return parsed;
  
  // Fallback: try direct parsing
  const date = new Date(timestamp);
  if (!isNaN(date.getTime())) return date;
  
  console.warn('Failed to parse timestamp:', timestamp);
  return new Date();
}

/**
 * Format a UTC timestamp to EDT timezone for display with full date and time
 */
function formatTimestampToEDT(timestamp: string | Date | null | undefined): string {
  const date = parseUtcTimestamp(timestamp);
  return date.toLocaleString('en-US', { 
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric', 
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'America/New_York'
  });
}

/**
 * Get participant role - handles both old API (role) and new API (kind) formats
 * Old API: role = 'human' | 'ai_friend'
 * New API: kind = 'user' | 'ai_friend'
 */
function getParticipantRole(p: any): 'user' | 'ai_friend' | string {
  // New API uses 'kind'
  if (p.kind) {
    return p.kind;
  }
  // Old API uses 'role' with 'human' instead of 'user'
  if (p.role === 'human') {
    return 'user';
  }
  return p.role || 'unknown';
}

/**
 * Get profile image URL - handles both old API (pfp_url) and new API (avatar_url) formats
 */
function getProfileImageUrl(p: any): string | undefined {
  return p.avatar_url || p.pfp_url || undefined;
}

/**
 * Determine if a message is from AI friend
 * Old API: msg.sender === 'ai_friend'
 * New API: msg.sender_type === 'agent'
 */
function isAiMessage(msg: any): boolean {
  // New API uses sender_type: 'agent' for AI, 'user' for human
  if (msg.sender_type) {
    const senderType = String(msg.sender_type).toLowerCase();
    return senderType === 'agent' || senderType === 'ai_friend';
  }
  // Old API uses sender: 'ai_friend'
  return msg.sender === 'ai_friend';
}

function coerceOptionalString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

export function transformBackendConversation(backendConv: any) {
  const processedConv = processConversation(backendConv);

  // Handle both old (role='human') and new (kind='user') API formats
  const humanParticipants = processedConv.participants?.filter((p: any) => {
    const role = getParticipantRole(p);
    return role === 'user' || role === 'human';
  }) || [];
  
  const aiParticipants = processedConv.participants?.filter((p: any) => {
    const role = getParticipantRole(p);
    return role === 'ai_friend';
  }) || [];

  let conversationName = `Conversation ${backendConv.id}`;
  if (!backendConv.is_group && humanParticipants.length > 0 && aiParticipants.length > 0) {
    conversationName = `${humanParticipants[0].name || 'User'} ↔ ${aiParticipants[0].name || 'AI Friend'}`;
  } else if (backendConv.is_group) {
    const allNames = backendConv.participants?.map((p: any) => p.name || 'Unknown').join(', ') || '';
    conversationName = allNames.length > 50 ? allNames.substring(0, 47) + '...' : allNames;
  } else if (humanParticipants.length > 0) {
    conversationName = humanParticipants[0].name || 'User';
  } else if (aiParticipants.length > 0) {
    conversationName = aiParticipants[0].name || 'AI Friend';
  }

  const primaryHuman = humanParticipants[0];
  const primaryAI = aiParticipants[0];

  const lastActivityByParticipant: Record<string, Date> = {};
  const messagesArray = Array.isArray(processedConv.messages) ? processedConv.messages : [];
  for (const msg of messagesArray) {
    const senderId = msg?.sender_id != null ? String(msg.sender_id) : undefined;
    const createdAt = msg?.created_at ? parseUtcTimestamp(msg.created_at) : undefined;
    if (!senderId || !createdAt || Number.isNaN(createdAt.getTime())) continue;
    if (!lastActivityByParticipant[senderId] || createdAt > lastActivityByParticipant[senderId]) {
      lastActivityByParticipant[senderId] = createdAt;
    }
  }

  let lastUserMessageTimeFromMessages: Date | undefined;
  for (const msg of messagesArray) {
    const senderTypeRaw = msg?.sender_type ?? msg?.sender ?? msg?.senderType ?? '';
    const senderType = String(senderTypeRaw || '').toLowerCase();
    if (senderType !== 'user' && senderType !== 'human') continue;

    const createdAtRaw = msg?.created_at ?? msg?.createdAt ?? msg?.timestamp;
    const parsed =
      createdAtRaw instanceof Date
        ? createdAtRaw
        : typeof createdAtRaw === 'string'
          ? (parseVectorDbUtc(createdAtRaw) ?? new Date(createdAtRaw))
          : undefined;
    if (!parsed || Number.isNaN(parsed.getTime())) continue;
    if (!lastUserMessageTimeFromMessages || parsed > lastUserMessageTimeFromMessages) {
      lastUserMessageTimeFromMessages = parsed;
    }
  }

  // Get profile images using new avatar_url or old pfp_url
  const humanProfileImage = primaryHuman ? getProfileImageUrl(primaryHuman) : undefined;
  const aiProfileImage = primaryAI ? getProfileImageUrl(primaryAI) : undefined;
  const lastMessageTime = processedConv.messages?.length > 0
    ? parseUtcTimestamp(processedConv.messages[processedConv.messages.length - 1].created_at)
    : (processedConv.last_message?.created_at 
        ? parseUtcTimestamp(processedConv.last_message.created_at) 
        : parseUtcTimestamp(processedConv.updated_at));
  const lastUserMessageRaw = processedConv.last_user_message_at ?? backendConv.last_user_message_at;
  const lastUserMessageTime = lastUserMessageRaw
    ? parseUtcTimestamp(lastUserMessageRaw)
    : lastUserMessageTimeFromMessages;

  return {
    id: processedConv.id.toString(),
    name: conversationName,
    phoneNumber: primaryAI?.phone || primaryHuman?.phone || '(555) 000-0000',
    userPhoneNumber: primaryHuman?.phone || '(555) 000-0000',
    profileImage: humanProfileImage || aiProfileImage,
    username: primaryHuman?.username,
    messages: foldReactionMessages((processedConv.messages || []).map((msg: any) => {
      // Parse timestamp as UTC and format for EDT display
      const msgDate = parseUtcTimestamp(msg.created_at);
      const timeString = formatTimestampToEDT(msg.created_at);

      const normalizeText = (value: string) => value.trim().replace(/\s+/g, ' ');
      const text = String(msg.content || '');
      const normalizedText = normalizeText(text);
      const createdAtIso = !Number.isNaN(msgDate.getTime()) ? msgDate.toISOString() : '';
      const senderKey =
        msg.sender_id != null
          ? String(msg.sender_id)
          : String(msg.sender || msg.sender_type || '');

      const messageHandle =
        msg.message_handle != null
          ? String(msg.message_handle)
          : msg.messageHandle != null
            ? String(msg.messageHandle)
            : undefined;
      const runId =
        msg.run_id != null
          ? String(msg.run_id)
          : msg.agent_run_id != null
            ? String(msg.agent_run_id)
            : undefined;
      const compositeId = `sc:${senderKey}|${createdAtIso}|${normalizedText}`;
      const stableId =
        msg.id?.toString() ||
        (messageHandle ? `h:${messageHandle}` : undefined) ||
        (runId ? `r:${runId}` : undefined) ||
        compositeId;
      const metadata = msg.metadata_json ?? undefined;
      const interactionType = coerceOptionalString(msg.interaction_type ?? metadata?.interaction_type);
      const feedItemId = coerceOptionalString(msg.feed_item_id ?? metadata?.feed_item_id);
      const postId = coerceOptionalString(msg.post_id ?? metadata?.post_id);
      const replyId = coerceOptionalString(msg.reply_id ?? metadata?.reply_id);
      
      return {
        id: stableId,
        text,
        timestamp: timeString,
        // AI messages (sender_type='agent' or sender='ai_friend') -> isOwn=true (blue bubbles, right side)
        // User messages (sender_type='user' or sender='human') -> isOwn=false (gray bubbles, left side)
        isOwn: isAiMessage(msg),
        createdAt: msgDate,
        messageType: msg.message_type || msg.messageType,
        sender: msg.sender || msg.sender_type,
        senderId: msg.sender_id != null ? String(msg.sender_id) : undefined,
        senderName: msg.sender_name,
        messageHandle,
        runId,
        metadata,
        interactionType,
        feedItemId,
        postId,
        replyId,
      };
    })),
    isGroup: processedConv.is_group || false,
    lastMessageTime,
    lastUserMessageTime,
    participants: (processedConv.participants || []).map((p: any) => {
      const pid = p.id?.toString() || Math.random().toString();
      const derivedLast = lastActivityByParticipant[String(p.id ?? '')];
      const backendLast = p.last_active ? parseUtcTimestamp(p.last_active) : undefined;
      const lastActive = backendLast && !Number.isNaN(backendLast.getTime())
        ? backendLast
        : (derivedLast && !Number.isNaN(derivedLast.getTime()) ? derivedLast : undefined);
      return {
        id: pid,
        name: p.name || 'Unknown',
        fname: p.fname,
        lname: p.lname,
        phoneNumber: p.phone || '',
        role: getParticipantRole(p), // Normalize role
        lastActive,
        profileImage: getProfileImageUrl(p), // Handle both avatar_url and pfp_url
        username: p.username,
      };
    }),
    unread: false,
    lastReadAt: null,
  };
}


